import { useState } from 'react';

import { Locale } from '@core/Entities/Locale/Locale.entity';
import { Aggregations } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import { VariantAtom } from '@core/Entities/Search/Atoms/AvailableVariantsAtom.entity';
import { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import { getLabelWithEllipsis } from '@core/Utils/Strings/GetLabelWithEllipsis';
import { BODY_TYPES } from '@core/Utils/VehicleMappedMessages/BodyTypeMappings.constants';
import { COLORS } from '@core/Utils/VehicleMappedMessages/ColorMappings.constants';
import { FUEL_TYPES } from '@core/Utils/VehicleMappedMessages/FuelTypeMappings.constants';
import { Button } from '@gds/Button/Button';
import { Collapse } from '@gds/Collapse/Collapse';

import { Typography } from '@gds/Typography/Typography';

import { PrefetchLink } from '@shared-components/PrefetchLink/PrefetchLink';

import { getOptionsWithAggs } from 'Utils/SearchFilters/GetOptionsWithAggs';

import styles from './MakeModelInterlinking.module.css';

type SectionOption = {
  id: string;
  count: number;
  label: string;
  labelKey?: string;
};

export const getTopOptions = (options: SectionOption[], limit = 6): SectionOption[] => {
  return options
    .filter(option => option.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

export const getAllOptions = (options: SectionOption[]): SectionOption[] => {
  return options.filter(option => option.count > 0).sort((a, b) => b.count - a.count);
};

// Extended aggregations type that includes the variants array from the atom
export interface ExtendedAggregations extends Aggregations {
  variants?: VariantAtom[];
}

export type SectionConfig = {
  key: string;
  headingKey: string;
  getOptions: (
    aggregations: ExtendedAggregations,
    make: string,
    modelWithMake: string | undefined,
  ) => SectionOption[];
  createFilters: (make: string, modelWithMake: string | undefined, optionId: string) => Filter[];
  formatCtaText: (
    makeDisplayName: string,
    modelDisplayName: string | undefined,
    optionDisplayName: string,
  ) => string;
  getDisplayName: (option: SectionOption, messages: Record<string, string>) => string;
  messageKey: 'body-types' | 'fuel-types' | 'transmissions' | 'colors' | null;
};

const modelsConfig: SectionConfig = {
  key: 'models',
  headingKey: 'modelsForSale',
  messageKey: null,
  getOptions: (aggs, make) => {
    if (!aggs['model']?.[make]?.models) return [];
    return Object.entries(aggs['model'][make].models)
      .map(([id, model]) => ({ id, count: model.count, label: model.label }))
      .filter(({ count }) => count > 0);
  },
  createFilters: (makeName, _, modelId) => [
    { id: 'make', value: makeName },
    { id: 'model', value: modelId },
  ],
  formatCtaText: (makeDisplay, _, id) => [makeDisplay, id].filter(Boolean).join(' '),
  getDisplayName: option => option.label || option.id,
};

const trimsConfig: SectionConfig = {
  key: 'trims',
  headingKey: 'modelVariants',
  messageKey: null,
  getOptions: (aggs, makeName, modelWithMakeName) => {
    // First try to use the flattened variants array
    if (aggs.variants && aggs.variants.length > 0) {
      const filteredVariants = aggs.variants.filter(
        (v: VariantAtom) => v.make === makeName && v.model === modelWithMakeName,
      );

      if (filteredVariants.length > 0) {
        return filteredVariants.map((variant: VariantAtom) => ({
          id: variant.key,
          count: variant.count,
          label: variant.displayName,
        }));
      }
    }

    // Fall back to using the nested variant structure if available
    if (modelWithMakeName && aggs.variant?.[makeName]?.models[modelWithMakeName]?.variants) {
      const variants = aggs.variant[makeName].models[modelWithMakeName].variants;
      return Object.entries(variants).reduce((acc, [variantId, variant]) => {
        if (variant.count > 0) {
          acc.push({ id: variantId, count: variant.count, label: variant.label });
        }
        return acc;
      }, [] as SectionOption[]);
    }

    return [];
  },
  createFilters: (makeName, modelWithMakeName, variantId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'variant-type', value: variantId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, trimDisplay) =>
    [makeDisplay, modelDisplay, trimDisplay].filter(Boolean).join(' '),
  getDisplayName: option => option.label || option.id,
};

const bodyTypesConfig: SectionConfig = {
  key: 'body-types',
  headingKey: 'bodyTypes',
  messageKey: 'body-types',
  getOptions: aggs => {
    if (!aggs['body-type']) return [];
    return getOptionsWithAggs(BODY_TYPES, aggs['body-type']);
  },
  createFilters: (makeName, modelWithMakeName, bodyTypeId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'body-type', value: bodyTypeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, bodyTypeDisplay) =>
    [makeDisplay, modelDisplay, bodyTypeDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.labelKey as keyof typeof messagesObj] || option.id,
};

const fuelTypesConfig: SectionConfig = {
  key: 'fuel-types',
  headingKey: 'fuelType',
  messageKey: 'fuel-types',
  getOptions: aggs => {
    if (!aggs['fuel-type']) return [];
    return getOptionsWithAggs(FUEL_TYPES, aggs['fuel-type']);
  },
  createFilters: (makeName, modelWithMakeName, fuelTypeId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'fuel-type', value: fuelTypeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, fuelTypeDisplay) =>
    [makeDisplay, modelDisplay, fuelTypeDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.labelKey as keyof typeof messagesObj] || option.id,
};

const filterOutTransmissions = new Set<string>(['unknown', 'none']);

const transmissionsConfig: SectionConfig = {
  key: 'transmissions',
  headingKey: 'transmissions',
  messageKey: 'transmissions',
  getOptions: aggs => {
    if (!aggs['gear-box']) return [];

    return Object.entries(aggs['gear-box'])
      .map(([id, count]) => ({ id, count: count as number, label: id }))
      .filter(({ count, id }) => count > 0 && !filterOutTransmissions.has(id));
  },
  createFilters: (makeName, modelWithMakeName, gearboxId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'gear-box', value: gearboxId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, gearboxDisplay) =>
    [makeDisplay, modelDisplay, gearboxDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.id as keyof typeof messagesObj] || option.id,
};

const engineSizesConfig: SectionConfig = {
  key: 'engine-sizes',
  headingKey: 'engineSizes',
  messageKey: null,
  getOptions: aggs => {
    if (!aggs['engine-size']) return [];
    return Object.entries(aggs['engine-size'])
      .map(([id, count]) => ({ id, count: count as number, label: id }))
      .filter(({ count }) => count > 0);
  },
  createFilters: (makeName, modelWithMakeName, engineSizeId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'engine-size__gte', value: engineSizeId },
    { id: 'engine-size__lte', value: engineSizeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, engineSizeDisplay) =>
    [makeDisplay, modelDisplay, engineSizeDisplay].filter(Boolean).join(' '),
  getDisplayName: option => option.id,
};

// "other" color has different ids in different locales
const otherColorIds = new Set(['othercolor', 'other']);

const colorsConfig: SectionConfig = {
  key: 'colors',
  headingKey: 'colors',
  messageKey: 'colors',
  getOptions: aggs => {
    if (!aggs.color) return [];
    const options = getOptionsWithAggs(COLORS, aggs.color).filter(
      option => !otherColorIds.has(option.id) && !otherColorIds.has(option.labelKey),
    );
    return options;
  },
  createFilters: (makeName, modelWithMakeName, colorId) => [
    { id: 'make', value: makeName },
    ...(modelWithMakeName ? [{ id: 'model' as const, value: modelWithMakeName }] : []),
    { id: 'color', value: colorId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, colorDisplay) =>
    [makeDisplay, modelDisplay, colorDisplay].filter(Boolean).join(' '),
  getDisplayName: (option, messagesObj) => messagesObj[`${option.labelKey}.one`] || option.id,
};

const CollapsibleSection = ({
  config,
  options,
  make,
  modelWithMake,
  locale,
  messages,
  messageDict,
  makeDisplayName,
  modelDisplayName,
}: {
  config: SectionConfig;
  options: SectionOption[];
  make: string;
  modelWithMake: string | undefined;
  locale: Locale;
  messages: Record<string, string>;
  messageDict: Record<string, string>;
  makeDisplayName: string;
  modelDisplayName: string | undefined;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleOptions = getTopOptions(options);
  const hasMoreOptions = options.length > visibleOptions.length;

  const createCtas = (optionsToUse: SectionOption[]) => {
    return optionsToUse.map(option => {
      const displayName = config.getDisplayName(option, messageDict);
      const filters = config.createFilters(make, modelWithMake, option.id);

      return {
        ctaText: config.formatCtaText(makeDisplayName, modelDisplayName, displayName),
        link: createPathFromFilters(filters, locale),
      };
    });
  };

  const visibleCtas = createCtas(visibleOptions);
  const hiddenCtas = hasMoreOptions ? createCtas(options.slice(visibleOptions.length)) : [];

  return (
    <div className={styles.collapsibleSection}>
      <LinkBlock key={config.key} heading={messages[config.headingKey]} ctas={visibleCtas} />

      {hasMoreOptions && (
        <>
          <Collapse open={isExpanded}>
            <div className={styles.collapsibleSectionContent}>
              <LinkBlock key={`${config.key}-more`} heading={null} ctas={hiddenCtas} />
            </div>
          </Collapse>

          <Button
            variant="link"
            onClick={() => setIsExpanded(!isExpanded)}
            className={styles.viewMoreButton}
          >
            {isExpanded ? messages.viewLess : messages.viewMore}
          </Button>
        </>
      )}
    </div>
  );
};

export const createSection = (
  config: SectionConfig,
  aggregations: ExtendedAggregations,
  make: string,
  modelWithMake: string | undefined,
  locale: Locale,
  messages: Record<string, string>,
  messageDict: Record<string, string>,
  makeDisplayName: string,
  modelDisplayName: string | undefined,
) => {
  const options = config.getOptions(aggregations, make, modelWithMake);
  const allOptions = getAllOptions(options);

  if (allOptions.length === 0) {
    return null;
  }

  return (
    <div
      key={config.key}
      className={styles.section}
      data-test-id={
        // Temporary fix to avoid breaking changes to the PLP.spec tests
        config.key === 'models' ? 'plp-all-models' : `${config.key}-interlinking-section`
      }
    >
      <CollapsibleSection
        config={config}
        options={allOptions}
        make={make}
        modelWithMake={modelWithMake}
        locale={locale}
        messages={messages}
        messageDict={messageDict}
        makeDisplayName={makeDisplayName}
        modelDisplayName={modelDisplayName}
      />
    </div>
  );
};

type LinkBlockProps = {
  heading: string | null;
  ctas: { ctaText: string; link: string; onClickFunc?: () => void }[];
};

const LinkBlock = (interlinking: LinkBlockProps) => {
  return (
    <div className={styles.section}>
      {interlinking.heading ? (
        <Typography className={styles.sectionHeading} Component="h3" variant="subheading2">
          {interlinking.heading}
        </Typography>
      ) : null}
      <ol className={styles.ctas} aria-label={interlinking.heading ?? ''}>
        {interlinking.ctas.map(cta => {
          return (
            <li key={cta.ctaText} aria-label={cta.ctaText}>
              <PrefetchLink
                href={cta.link ?? '/'}
                className={styles.links}
                onClick={cta.onClickFunc}
              >
                {getLabelWithEllipsis(cta.ctaText, 30, 27)}
              </PrefetchLink>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const MAKE_MODEL_INTERLINKING_SECTIONS: SectionConfig[] = [
  trimsConfig,
  bodyTypesConfig,
  fuelTypesConfig,
  transmissionsConfig,
  engineSizesConfig,
  colorsConfig,
];

const MAKE_INTERLINKING_SECTIONS: SectionConfig[] = [
  modelsConfig,
  bodyTypesConfig,
  transmissionsConfig,
  fuelTypesConfig,
];

export const getInterlinkingSections = ({ model }: { model: string | undefined }) => {
  return model ? MAKE_MODEL_INTERLINKING_SECTIONS : MAKE_INTERLINKING_SECTIONS;
};
